import { useEffect, useState } from 'react';
import {
  createMachine,
  state,
  state as final,
  state as initial,
  transition,
  reduce,
  guard,
  invoke,
} from 'robot3';
import { createUseMachine } from 'robot-hooks';
import { submit } from './functions';
import { ACTION_TYPES, STATE } from './constants.js';
import {
  selectedDry,
  isMixed,
  isFresh,
  hasFreshInCart,
  hasDryInCart,
  isFreshUrl,
  isBakedUrl,
  submitCartFailed,
  prevStateFresh,
  prevStateBaked,
} from './guard';

export const usePlanSelectionMachine = (context = {}) =>
  useMachine(planSelectionMachine, context);

const useMachine = createUseMachine(useEffect, useState);

const createInitialContext = ({
  recipes = { fresh: null, dry: null },
  mealPrepType = null,
} = {}) => ({
  recipes,
  mealPrepType,
});

const planSelectionMachine = createMachine(
  STATE.initial,
  {
    [STATE.initial]: initial(
      transition(
        ACTION_TYPES.initialLoad,
        STATE.fresh,
        guard(isFreshUrl),
        guard(hasFreshInCart),
        reduce((ctx, { payload }) => ({
          ...ctx,
          recipes: payload.recipes ?? ctx.recipes,
          mealPrepType: payload.mealPrepType ?? ctx.mealPrepType,
        })),
      ),
      transition(
        ACTION_TYPES.initialLoad,
        STATE.baked,
        guard(isBakedUrl),
        guard(hasDryInCart),
        reduce((ctx, { payload }) => ({
          ...ctx,
          recipes: payload.recipes ?? ctx.recipes,
          mealPrepType: payload.mealPrepType ?? ctx.mealPrepType,
        })),
      ),
      transition(
        ACTION_TYPES.initialLoad,
        STATE.mealprep,
        reduce((ctx, { payload }) => ({
          ...ctx,
          recipes: payload.recipes ?? ctx.recipes,
          mealPrepType: payload.mealPrepType ?? ctx.mealPrepType,
        })),
      ),
    ),
    [STATE.mealprep]: state(
      transition(
        ACTION_TYPES.selectedMealprep,
        STATE.baked,
        guard(selectedDry),
        reduce((ctx, { payload }) => ({
          ...ctx,
          mealPrepType: payload.mealPrepType,
          recipes: {
            ...ctx.recipes,
            fresh: null,
          },
        })),
      ),
      transition(
        ACTION_TYPES.selectedMealprep,
        STATE.fresh,
        reduce((ctx, { payload }) => {
          const recipes = { ...ctx.recipes };
          if (payload.mealPrepType === 'fresh') recipes.dry = null;
          return {
            ...ctx,
            mealPrepType: payload.mealPrepType,
            recipes,
          };
        }),
      ),
    ),
    [STATE.fresh]: state(
      transition(
        ACTION_TYPES.selectedFreshRecipes,
        STATE.submitCart,
        guard(isFresh),
        reduce((ctx, { payload }) => ({
          ...ctx,
          recipes: { ...ctx.recipes, fresh: payload.recipes },
          prevState: STATE.fresh,
        })),
      ),
      transition(
        ACTION_TYPES.selectedFreshRecipes,
        STATE.baked,
        guard(isMixed),
        reduce((ctx, { payload }) => ({
          ...ctx,
          recipes: { ...ctx.recipes, fresh: payload.recipes },
        })),
      ),
      transition(ACTION_TYPES.goBack, STATE.mealprep),
    ),
    [STATE.baked]: state(
      transition(
        ACTION_TYPES.selectedBakedRecipes,
        STATE.submitCart,
        reduce((ctx, { payload }) => ({
          ...ctx,
          recipes: { ...ctx.recipes, dry: payload.recipes },
          prevState: STATE.baked,
        })),
      ),
      transition(ACTION_TYPES.goBack, STATE.fresh, guard(isMixed)),
      transition(ACTION_TYPES.goBack, STATE.mealprep),
    ),
    [STATE.submitCart]: invoke(
      submit,
      transition(
        'done', //this action type is inherent to the invoke function
        STATE.fresh,
        guard(submitCartFailed),
        guard(prevStateFresh),
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
        })),
      ),
      transition(
        'done', // this action type is inherent to the invoke function
        STATE.baked,
        guard(submitCartFailed),
        guard(prevStateBaked),
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
        })),
      ),
      transition(
        'done', // this action type is inherent to the invoke function
        STATE.checkout,
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
        })),
      ),
    ),
    [STATE.checkout]: final(),
  },
  createInitialContext,
);
